import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import Button from '../index';
import colors from '../../../styles/colors';

const PILL_BUTTON_BORDER_RADIUS = '36px';

const StyledPillButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  border-radius: ${PILL_BUTTON_BORDER_RADIUS};
  cursor: pointer;
  white-space: nowrap;
  text-transform: none;
  box-shadow: none;

  &:hover {
    box-shadow: none;
  }

  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return css`
          font-size: 11px;
          height: 22px;
          padding: 0 10px;
        `;
      case 'medium':
      default:
        return css`
          font-size: 14px;
          padding: 5px 15px;
        `;
    }
  }}

  ${({ $variant }) => {
    switch ($variant) {
      case 'info':
        return css`
          border: 1px solid ${colors.shades.secondary7};
          background-color: ${colors.shades.secondary5};
          color: ${colors.base.secondary2};

          &:hover:not(:disabled) {
            background-color: ${colors.shades.secondary4};
          }
        `;
      // The default pill: every caller uses it as the ordinary action (Broadcast, Set
      // Reminder, Create Task). It was called 'warning' and filled with warning1 cream,
      // a holdover from the yellow theme where cream sat naturally under a yellow bar.
      // Against mint it read as an off-palette patch, and the name described a warning
      // appearance it no longer has, so both the look and the name are now neutral.
      case 'neutral':
      default:
        return css`
          border: 1px solid ${colors.shades.secondary6};
          background-color: ${colors.base.alpha};
          color: ${colors.base.secondary2};

          &:hover:not(:disabled) {
            background-color: ${colors.shades.secondary5};
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: ${({ $size }) => ($size === 'small' ? '12px' : '16px')};
    height: ${({ $size }) => ($size === 'small' ? '12px' : '16px')};
  }
`;

const PillButton = ({
  onClick,
  children,
  disabled,
  className,
  icon,
  variant,
  size,
  full,
}) => (
  <StyledPillButton
    onClick={onClick}
    startIcon={icon}
    disabled={disabled}
    className={className}
    $variant={variant}
    $size={size}
    full={full}
  >
    {children}
  </StyledPillButton>
);

PillButton.propTypes = {
  children: PropTypes.node,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  icon: PropTypes.element,
  className: PropTypes.string,
  variant: PropTypes.oneOf(['neutral', 'info']),
  size: PropTypes.oneOf(['small', 'medium']),
  full: PropTypes.bool,
};

PillButton.defaultProps = {
  children: null,
  onClick: null,
  disabled: false,
  icon: null,
  className: '',
  variant: 'neutral',
  size: 'medium',
  full: false,
};

export default PillButton;
